const PIVA_REGEX = /^\d{11}$/;

export function isValidPartitaIva(value: string | undefined | null): boolean {
  if (!value || !PIVA_REGEX.test(value)) {
    return false;
  }
  let sum = 0;
  for (let i = 0; i < 11; i += 1) {
    const digit = Number(value[i]);
    if (i % 2 === 0) {
      sum += digit;
    } else {
      const doubled = digit * 2;
      sum += doubled > 9 ? doubled - 9 : doubled;
    }
  }
  return sum % 10 === 0;
}

export function isValidCap(value: string | undefined | null): boolean {
  return !!value && /^\d{5}$/.test(value);
}

/**
 * Canonical Italian VAT normalization: strips non-alphanumeric characters,
 * uppercases, and drops an optional leading "IT" country prefix. Single
 * source of truth shared by the @IsPartitaIva decorator (frontend + backend
 * via classValidatorResolver) and backend persistence/normalizers.
 */
export function normalizeVatNumber(vat: string): string {
  const cleaned = vat.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  return cleaned.startsWith('IT') ? cleaned.slice(2) : cleaned;
}

const ELEVEN_DIGITS_REGEX = /^\d{11}$/;
/** Non-Italian EU/extra-EU VAT: country code + 2..13 alphanumerics. */
const FOREIGN_VAT_REGEX = /^[A-Z]{2}[A-Z0-9]{2,13}$/;

/**
 * Format-only check (no checksum) shared by `@IsPartitaIvaFormat` and the
 * company-profile form: an Italian VAT is exactly 11 digits, a foreign one
 * (country-code prefix) keeps its own national length.
 */
export function isValidVatNumberFormat(value: string): boolean {
  const normalized = normalizeVatNumber(value);
  return ELEVEN_DIGITS_REGEX.test(normalized) || FOREIGN_VAT_REGEX.test(normalized);
}

/**
 * Italian P.IVA with a valid check digit, after canonical normalization
 * (spaces, dots and the "IT" prefix are tolerated).
 */
export function isValidItalianVatNumber(value: string | undefined | null): boolean {
  return !!value && isValidPartitaIva(normalizeVatNumber(value));
}

/** Same VAT after canonical normalization; false when either side is empty. */
export function isSameVatNumber(
  first: string | undefined | null,
  second: string | undefined | null,
): boolean {
  if (!first || !second) {
    return false;
  }
  const normalizedFirst = normalizeVatNumber(first);
  return normalizedFirst !== '' && normalizedFirst === normalizeVatNumber(second);
}

/**
 * True when a VAT read off a document (AI/OCR) cannot be trusted as a supplier
 * key: an Italian-shaped value with the wrong length or check digit, or text
 * that is no VAT at all (e.g. a CAP + town). Foreign VATs (country prefix +
 * digits) are not judged: their checksum rules are national.
 */
export function extractedVatNeedsReview(value: string | undefined | null): boolean {
  if (!value) {
    return false;
  }
  const normalized = normalizeVatNumber(value);
  if (normalized === '') {
    return false;
  }
  if (FOREIGN_VAT_REGEX.test(normalized) && /\d/.test(normalized)) {
    return false;
  }
  return !isValidPartitaIva(normalized);
}
